import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search, MapPin, Bed, Bath, Square, TrendingUp,
  ArrowRight, Building2, Loader2, Lock, Clock, Eye,
  DollarSign, LogIn
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import logger from "@/utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const formatPrice = (price) => {
  if (!price) return "N/A";
  if (price >= 1000000) return `$${(price / 1000000).toFixed(2)}M`;
  return `$${price.toLocaleString()}`;
};

const formatParam = (key, val) => {
  if (!val || val === "all") return null;
  const labels = {
    sale_price: "Exchange Amount",
    market: "Market",
    min_beds: "Min Bedrooms",
    min_cap_rate: "Min Cap Rate",
  };
  let display = val;
  if (key === "sale_price" || key === "max_price") display = formatPrice(parseFloat(val));
  if (key === "market") display = val === "nyc" ? "New York City" : val === "miami" ? "Miami" : val;
  if (key === "min_beds") display = `${val}+`;
  if (key === "min_cap_rate") display = `${val}%+`;
  return { label: labels[key] || key, value: display };
};

export default function SharedSearch() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [error, setError] = useState(null);

  // Check auth status
  useEffect(() => {
    (async () => {
      try {
        await axios.get(`${API}/auth/me`, { withCredentials: true });
        setIsAuthenticated(true);
      } catch (err) {
        logger.error("Auth check failed:", err);
        setIsAuthenticated(false);
      } finally {
        setAuthChecked(true);
      }
    })();
  }, []);

  // Fetch meta (public, no auth)
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/replacement-finder/shared/${shareId}/meta`);
        setMeta(res.data);
      } catch (err) {
        logger.error("Failed to fetch shared search meta:", err);
        setError("This shared search was not found or has expired.");
      } finally {
        setLoading(false);
      }
    })();
  }, [shareId]);

  // Fetch full results when authenticated
  const fetchResults = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await axios.get(`${API}/replacement-finder/shared/${shareId}`, { withCredentials: true });
      setResults(res.data);
    } catch (err) {
      logger.error("Failed to load search results:", err);
      setError("Failed to load search results.");
    }
  }, [shareId, isAuthenticated]);

  useEffect(() => {
    if (authChecked && isAuthenticated && meta) {
      fetchResults();
    }
  }, [authChecked, isAuthenticated, meta, fetchResults]);

  const handleLogin = () => {
    const authUrl = `https://demobackend.emergentagent.com/auth/v1/env/oauth/google?redirect_url=${encodeURIComponent(window.location.href)}`;
    window.location.href = authUrl;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center max-w-md">
          <Search className="w-14 h-14 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Search Not Found</h2>
          <p className="text-slate-500 mb-6">{error}</p>
          <Link to="/replacement-finder">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white">Start a New Search</Button>
          </Link>
        </div>
      </div>
    );
  }

  const searchParams = meta?.search_params || {};
  const paramItems = Object.entries(searchParams)
    .map(([k, v]) => formatParam(k, v))
    .filter(Boolean);

  const sharedDate = meta?.created_at ? new Date(meta.created_at).toLocaleDateString("en-US", {
    year: "numeric", month: "long", day: "numeric"
  }) : "";

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header banner */}
      <div className="bg-gradient-to-r from-slate-900 to-blue-900 text-white py-10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center gap-2 text-blue-300 text-sm mb-3">
            <Clock className="w-4 h-4" />
            <span>Shared on {sharedDate}</span>
            {meta?.view_count > 0 && (
              <>
                <span className="text-slate-500 mx-1">|</span>
                <Eye className="w-4 h-4" />
                <span>{meta.view_count} view{meta.view_count !== 1 ? "s" : ""}</span>
              </>
            )}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3 text-white" data-testid="shared-search-title">
            1031 Replacement Property Search
          </h1>
          <p className="text-slate-300 mb-5">
            Live results based on the criteria below — updated in real time from our inventory.
          </p>
          {paramItems.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="shared-search-params">
              {paramItems.map(({ label, value }) => (
                <span key={label} className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-3 py-1 text-sm">
                  <span className="text-slate-400">{label}:</span>
                  <span className="font-medium text-white">{value}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Content area */}
      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Login gate */}
        {authChecked && !isAuthenticated && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg mx-auto text-center py-16">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-10">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-5">
                <Lock className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-3" data-testid="login-gate-title">
                Sign in to view results
              </h2>
              <p className="text-slate-500 mb-6 leading-relaxed">
                Register or sign in with Google to view the full property details, financial analysis, and metrics for this shared search.
              </p>
              <Button
                onClick={handleLogin}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-base"
                data-testid="shared-login-btn"
              >
                <LogIn className="w-5 h-5 mr-2" />
                Sign in with Google
              </Button>
              <p className="text-xs text-slate-400 mt-4">Free account — no credit card required</p>
            </div>
          </motion.div>
        )}

        {/* Authenticated results */}
        {isAuthenticated && results && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* Summary */}
            {results.count > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                  <p className="text-sm text-slate-500 mb-1">Matching Properties</p>
                  <p className="text-3xl font-bold text-slate-900" data-testid="shared-results-count">{results.count}</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                  <p className="text-sm text-slate-500 mb-1">Avg. Cap Rate</p>
                  <p className="text-3xl font-bold text-green-600">{results.avg_cap_rate}%</p>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                  <p className="text-sm text-slate-500 mb-1">Price Range</p>
                  <p className="text-xl font-bold text-slate-900">
                    {results.price_range ? `${formatPrice(results.price_range.min)} - ${formatPrice(results.price_range.max)}` : "N/A"}
                  </p>
                </div>
              </div>
            )}

            {/* Property cards */}
            {results.count === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
                <Search className="w-14 h-14 text-slate-200 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No matching properties currently available</h3>
                <p className="text-slate-500">The search criteria may no longer match any active inventory.</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="shared-results-list">
                {results.properties.map((prop, idx) => (
                  <motion.div
                    key={prop.property_id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: idx * 0.04 }}
                    className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                  >
                    <div className="flex flex-col md:flex-row">
                      <div className="w-full md:w-56 h-40 md:h-auto bg-slate-100 flex-shrink-0">
                        {prop.images?.[0] ? (
                          <img src={prop.images[0]} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Building2 className="w-10 h-10 text-slate-300" /></div>
                        )}
                      </div>
                      <div className="flex-1 p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <h3 className="font-semibold text-slate-900 text-lg">{prop.title}</h3>
                            <div className="flex items-center gap-1.5 text-sm text-slate-500 mt-1">
                              <MapPin className="w-3.5 h-3.5" /><span>{prop.address || prop.location}</span>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-xl font-bold text-slate-900">{formatPrice(prop.price)}</p>
                            <span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-100 text-slate-600">{prop.market?.toUpperCase()}</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-slate-600">
                          {prop.bedrooms > 0 && <span className="flex items-center gap-1"><Bed className="w-4 h-4 text-slate-400" />{prop.bedrooms} bed</span>}
                          {prop.bathrooms > 0 && <span className="flex items-center gap-1"><Bath className="w-4 h-4 text-slate-400" />{prop.bathrooms} bath</span>}
                          {prop.sqft > 0 && <span className="flex items-center gap-1"><Square className="w-4 h-4 text-slate-400" />{prop.sqft?.toLocaleString()} SF</span>}
                        </div>
                        <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
                          {prop.monthly_rent > 0 && <div><span className="text-slate-400">Rent:</span><span className="ml-1 font-semibold text-slate-700">${prop.monthly_rent.toLocaleString()}/mo</span></div>}
                          {prop.noi !== 0 && <div><span className="text-slate-400">NOI:</span><span className="ml-1 font-semibold text-green-600">${prop.noi.toLocaleString()}/yr</span></div>}
                          <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1">
                            <TrendingUp className="w-4 h-4 text-green-600" /><span className="font-bold text-green-700">{prop.cap_rate}% Cap</span>
                          </div>
                        </div>
                        <div className="mt-4">
                          <Link to={`/properties/${prop.property_id}`}>
                            <Button variant="outline" size="sm" className="text-blue-600 border-blue-200 hover:bg-blue-50">
                              View Details <ArrowRight className="w-4 h-4 ml-1.5" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Footer context */}
            <div className="mt-8 flex flex-col md:flex-row gap-4">
              <div className="flex-1 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  <Clock className="w-4 h-4 inline mr-1.5 -mt-0.5" />
                  <strong>Live results</strong> — this search runs against our current inventory. Originally shared on {sharedDate}.
                </p>
              </div>
              <Link to="/replacement-finder" className="flex-shrink-0">
                <Button variant="outline" className="h-full border-slate-300">
                  <Search className="w-4 h-4 mr-2" />
                  Run Your Own Search
                </Button>
              </Link>
            </div>

            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-700 leading-relaxed">
                <strong>Disclaimer:</strong> Krieger Real Estate LLC is a licensed real estate brokerage. All financial figures are estimates and are subject to change. This does not constitute legal, tax, or financial advice. Buyers should conduct their own independent due diligence.
              </p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
